import { useEffect, useMemo, useRef, useState } from 'react';
import { ApplicationTargetEnum } from '~types/app-selection';
import {
    areProvincesEqual,
    getDefaultProvinces,
    resolveProvincesSelection,
} from './province-selection-utils';

type UseRegionProvincesParams = {
    appTarget: ApplicationTargetEnum | undefined;
    defaultProvincesCsv?: string;
    queryProvinces?: string | string[];
    setProvincesParam: (value: string | undefined) => void;
};

const toProvincesCsv = (provinces: string[]) =>
    provinces.length > 0 ? provinces.join(',') : undefined;

export const useRegionProvinces = ({
    appTarget,
    defaultProvincesCsv,
    queryProvinces,
    setProvincesParam,
}: UseRegionProvincesParams) => {
    const isQcTarget = appTarget === ApplicationTargetEnum.QC;

    const normalizedQueryCsv = useMemo(() => {
        if (Array.isArray(queryProvinces)) {
            return queryProvinces.join(',');
        }
        return queryProvinces;
    }, [queryProvinces]);

    const defaultProvinces = useMemo(
        () => getDefaultProvinces(appTarget, defaultProvincesCsv),
        [appTarget, defaultProvincesCsv]
    );

    const querySelection = useMemo(() => {
        if (!normalizedQueryCsv) {
            return [];
        }
        return getDefaultProvinces(appTarget, normalizedQueryCsv);
    }, [appTarget, normalizedQueryCsv]);

    const [selection, setSelection] = useState<string[]>(() =>
        resolveProvincesSelection({
            isQcTarget,
            defaultProvinces,
            selection: querySelection,
        })
    );
    const previousAppTargetRef = useRef(appTarget);

    useEffect(() => {
        if (previousAppTargetRef.current === appTarget) {
            return;
        }
        previousAppTargetRef.current = appTarget;
        if (!areProvincesEqual(selection, defaultProvinces)) {
            setSelection(defaultProvinces);
        }
    }, [appTarget, defaultProvinces, selection]);

    const selectedProvinces = useMemo(
        () =>
            resolveProvincesSelection({
                isQcTarget,
                defaultProvinces,
                selection,
            }),
        [defaultProvinces, isQcTarget, selection]
    );

    useEffect(() => {
        if (!areProvincesEqual(selection, selectedProvinces)) {
            setSelection(selectedProvinces);
        }
    }, [selection, selectedProvinces]);

    const provincesValue = toProvincesCsv(selectedProvinces);

    // A `provinces` that differs from both the last one seen and the region
    // last held here came from outside (Back, a link). It is adopted during
    // render, so nothing below mounts or fetches with the old region first.
    const [last, setLast] = useState({
        seen: normalizedQueryCsv,
        synced: provincesValue,
    });
    if (normalizedQueryCsv !== last.seen || provincesValue !== last.synced) {
        let synced = provincesValue;
        if (
            normalizedQueryCsv !== last.seen &&
            normalizedQueryCsv !== last.synced
        ) {
            const fromQuery = resolveProvincesSelection({
                isQcTarget,
                defaultProvinces,
                selection: querySelection,
            });
            if (!areProvincesEqual(selection, fromQuery)) {
                setSelection(fromQuery);
            }
            synced = toProvincesCsv(fromQuery);
        }
        setLast({ seen: normalizedQueryCsv, synced });
    }

    useEffect(() => {
        if (provincesValue !== normalizedQueryCsv) {
            setProvincesParam(provincesValue);
        }
    }, [normalizedQueryCsv, provincesValue, setProvincesParam]);

    const provincesParam =
        selectedProvinces.length > 0
            ? selectedProvinces.join(',')
            : defaultProvincesCsv;

    return {
        isQcTarget,
        selectedProvinces,
        setSelectedProvinces: setSelection,
        provincesParam,
    };
};
